<script setup lang="ts">
import { onMounted } from "vue";
import { useRoute } from "vue-router";

import { trackLogout } from "@/analytics/init";
import { closeConnection } from "@/backend/Connection";
import Auth from "@/backend/http/auth";
import { clearUserCache } from "@/backend/http/user";
import BaseSpinner from "@/components/ui/BaseSpinner.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { resetSentryUser } from "@/error/sentry";
import { goToLogin } from "@/router/navigation";
import { clearStores } from "@/router/sessionData";
import type { LogoutReason } from "@/router/types";
import { useUserStore } from "@/store/user";

const route = useRoute();

onMounted(async () => {
  const logoutReason = (route.query.reason || "unknown") as LogoutReason;
  clearUserData(logoutReason);

  try {
    const res = await Auth.logout(logoutReason);
    if (res.data.redirectUrl) {
      window.location.assign(res.data.redirectUrl);
    } else {
      login();
    }
  } catch (_) {
    login();
  }
});

function clearUserData(logoutReason: LogoutReason) {
  clearUserCache();
  useUserStore().resetUser();
  resetSentryUser();
  window.Intercom("shutdown");
  trackLogout(logoutReason);
}

function login() {
  void closeConnection();
  clearStores();
  void goToLogin();
}
</script>

<template>
  <div class="logout-page">
    <SvgIcon name="general/logo-named" class="logo" />
    <div class="content">
      <h1 class="title">{{ $t("logoutPage.title") }}</h1>
      <p class="description">{{ $t("logoutPage.description") }}</p>
      <BaseSpinner />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";

.logout-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: colors.$background-ui;
}

.logo {
  position: absolute;
  top: 80px;
  width: 109px;
  height: 20px;
  color: colors.$main;

  --logo-inner: #{colors.$background-ui};
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.title {
  @include typography.extra-large;

  margin: 0 0 8px;
  color: colors.$text;
}

.description {
  @include typography.medium("semi-bold");

  margin: 0 0 24px;
  color: colors.$text-subtle;
}
</style>
